import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { todayIST } from '../utils/time';

export default function DailyReportScheduleModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSmtp, setShowSmtp] = useState(false);

  const [form, setForm] = useState({
    enabled: true,
    time: '23:00',
    recipients: '',
    includeSummary: true,
    includeMissed: true,
    includeCompleted: true,
    includeIncidents: true,
    includeGuards: true,
    includeKpis: true,
    smtp_host: 'smtp.gmail.com',
    smtp_port: '587',
    smtp_user: '',
    smtp_pass: '',
    smtp_from: '',
    hasSmtpPassword: false,
  });

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      axios.get('/api/reports/daily/schedule')
        .then(res => {
          setForm(prev => ({
            ...prev,
            ...res.data,
            smtp_host: res.data.host || 'smtp.gmail.com',
            smtp_port: String(res.data.port || '587'),
            smtp_user: res.data.user || '',
            smtp_pass: res.data.pass || '',
            hasSmtpPassword: res.data.hasSmtpPassword,
          }));
        })
        .catch(() => toast.error('Could not load schedule settings'))
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.enabled && !form.recipients.trim()) {
      return toast.error('Please specify at least one recipient email address.');
    }
    setLoading(true);
    try {
      await axios.post('/api/reports/daily/schedule', form);
      toast.success('Daily report schedule saved successfully!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save schedule settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTest = async () => {
    if (!form.recipients.trim()) {
      return toast.error('Please enter a recipient email before sending a test.');
    }
    setTesting(true);
    try {
      const res = await axios.post('/api/reports/daily/send-email', {
        date: todayIST(),
        recipients: form.recipients,
        options: {
          includeSummary: form.includeSummary,
          includeMissed: form.includeMissed,
          includeCompleted: form.includeCompleted,
          includeIncidents: form.includeIncidents,
          includeGuards: form.includeGuards,
          includeKpis: form.includeKpis,
        },
      });
      toast.success(res.data.message || 'Daily report email sent successfully!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send report email. Check SMTP settings.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      padding: 16
    }}>
      <div style={{
        background: '#ffffff', borderRadius: 14, width: '100%', maxWidth: 580,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        maxHeight: '90vh', overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#0f172a', color: '#ffffff', borderRadius: '14px 14px 0 0'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#38bdf8' }}>
              ⏰ Daily Patrol Report & Email Scheduling
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: '#94a3b8' }}>
              Configure automated daily email delivery of completed and missed checkpoints.
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        <form onSubmit={handleSave} style={{ padding: '20px 24px' }}>
          {/* Enable Toggle */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', background: form.enabled ? '#eff6ff' : '#f8fafc',
            border: `1.5px solid ${form.enabled ? '#93c5fd' : '#e2e8f0'}`,
            borderRadius: 10, marginBottom: 18
          }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
                Automated Daily Email Dispatch
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                {form.enabled ? 'Active — Report will send automatically at scheduled time' : 'Disabled — No automated emails will be sent'}
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={e => setForm({ ...form, enabled: e.target.checked })}
                style={{ width: 18, height: 18, accentColor: '#2563eb' }}
              />
            </label>
          </div>

          {/* Time Picker */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
              🕒 Scheduled Daily Sending Time (24h IST)
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="time"
                value={form.time}
                onChange={e => setForm({ ...form, time: e.target.value })}
                required
                style={{
                  padding: '8px 12px', borderRadius: 8, border: '1.5px solid #cbd5e1',
                  fontSize: 14, fontWeight: 600, width: 140
                }}
              />
              <div style={{ display: 'flex', gap: 4 }}>
                {['08:00', '20:00', '23:00', '23:59'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm({ ...form, time: t })}
                    style={{
                      padding: '5px 10px', borderRadius: 6, fontSize: 11, border: '1px solid #e2e8f0',
                      background: form.time === t ? '#2563eb' : '#f8fafc',
                      color: form.time === t ? '#ffffff' : '#475569',
                      cursor: 'pointer', fontWeight: form.time === t ? 600 : 400
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Recipient Emails */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
              📧 Recipient Email Address(es)
            </label>
            <input
              type="text"
              placeholder="e.g. manager@patrol.com, supervisor@patrol.com"
              value={form.recipients}
              onChange={e => setForm({ ...form, recipients: e.target.value })}
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #cbd5e1',
                fontSize: 13, boxSizing: 'border-box'
              }}
            />
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              Separate multiple email addresses with commas (,).
            </div>
          </div>

          {/* User Selectable Information Checkboxes */}
          <div style={{
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
            padding: '14px 16px', marginBottom: 18
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
              📋 Select Information to Include in Report:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.includeMissed}
                  onChange={e => setForm({ ...form, includeMissed: e.target.checked })}
                  style={{ accentColor: '#dc2626' }}
                />
                <span style={{ fontWeight: 600, color: '#dc2626' }}>🔴 Missed Checkpoints</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.includeCompleted}
                  onChange={e => setForm({ ...form, includeCompleted: e.target.checked })}
                  style={{ accentColor: '#16a34a' }}
                />
                <span style={{ fontWeight: 500, color: '#16a34a' }}>🟢 Verified Checkpoints</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.includeSummary}
                  onChange={e => setForm({ ...form, includeSummary: e.target.checked })}
                  style={{ accentColor: '#2563eb' }}
                />
                <span style={{ fontWeight: 500, color: '#334155' }}>📋 Patrols Summary List</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.includeKpis}
                  onChange={e => setForm({ ...form, includeKpis: e.target.checked })}
                  style={{ accentColor: '#2563eb' }}
                />
                <span style={{ fontWeight: 500, color: '#334155' }}>📊 Executive KPI Badges</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.includeIncidents}
                  onChange={e => setForm({ ...form, includeIncidents: e.target.checked })}
                  style={{ accentColor: '#ea580c' }}
                />
                <span style={{ fontWeight: 500, color: '#ea580c' }}>🚨 Security Incidents</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.includeGuards}
                  onChange={e => setForm({ ...form, includeGuards: e.target.checked })}
                  style={{ accentColor: '#2563eb' }}
                />
                <span style={{ fontWeight: 500, color: '#334155' }}>👮 Guard Attendance</span>
              </label>
            </div>
          </div>

          {/* SMTP Mailer Configuration Toggle */}
          <div style={{ marginBottom: 18 }}>
            <button
              type="button"
              onClick={() => setShowSmtp(!showSmtp)}
              style={{
                background: 'none', border: 'none', color: '#2563eb',
                fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0
              }}
            >
              {showSmtp ? '▼ Hide Email Server (SMTP) Settings' : '▶ Configure Email Server (Gmail / SMTP Settings)'}
            </button>

            {showSmtp && (
              <div style={{
                marginTop: 10, padding: 14, background: '#f8fafc',
                border: '1px solid #e2e8f0', borderRadius: 8
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 8 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>SMTP Host</label>
                    <input
                      type="text"
                      value={form.smtp_host}
                      onChange={e => setForm({ ...form, smtp_host: e.target.value })}
                      placeholder="smtp.gmail.com"
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Port</label>
                    <input
                      type="text"
                      value={form.smtp_port}
                      onChange={e => setForm({ ...form, smtp_port: e.target.value })}
                      placeholder="587"
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Sender Email / Username</label>
                  <input
                    type="email"
                    value={form.smtp_user}
                    onChange={e => setForm({ ...form, smtp_user: e.target.value })}
                    placeholder="your-email@gmail.com"
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>
                    Password or App Password {form.hasSmtpPassword && <span style={{ color: '#16a34a' }}>✓ Saved</span>}
                  </label>
                  <input
                    type="password"
                    value={form.smtp_pass}
                    onChange={e => setForm({ ...form, smtp_pass: e.target.value })}
                    placeholder="Google 16-character App Password"
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
            <button
              type="button"
              onClick={handleSendTest}
              disabled={testing || !form.recipients}
              style={{
                padding: '8px 14px', borderRadius: 7, border: '1px solid #2563eb',
                background: '#ffffff', color: '#2563eb', fontWeight: 600, fontSize: 12,
                cursor: testing ? 'not-allowed' : 'pointer'
              }}
            >
              {testing ? '⏳ Sending Test...' : '📧 Send Test Email Now'}
            </button>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '8px 14px', borderRadius: 7, border: '1px solid #cbd5e1',
                  background: '#ffffff', color: '#475569', fontSize: 13, cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '8px 18px', borderRadius: 7, border: 'none',
                  background: '#2563eb', color: '#ffffff', fontWeight: 600, fontSize: 13,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Saving…' : '💾 Save Schedule'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
